import styled, { css } from 'styled-components'

import { iconButtonStyles } from 'components/IconButton/styles'
import { color, font, fontWeight, SubheadlineStyle } from 'styles/global'
import { space } from 'styles/utils'

export const classNamePrefix = 'react-select__embrace'

export const selectStyles = (dark?: boolean, positionRight?: boolean) => css`
  --control-border: ${color('tertiaryBorder')};
  --control-color: ${color('interactive')};
  --control-color-selected: ${color('interactiveHover')};
  --control-min-height: ${space(11)};
  --control-value-left-padding: calc(${space(4)} - 2px);
  --control-value-right-padding: 0;

  .${classNamePrefix}__menu {
    right: ${positionRight ? 0 : 'unset'};
    margin-top: ${space(5)};
    padding-bottom: ${space(3)};
    width: calc(320px - ${space(8)}); // Minium screen width minus the padding
    background-color: ${dark
      ? color('overlayBackgroundSecondary')
      : color('overlayBackground')};
    &:before,
    &:after {
      content: '';
      width: auto;
      height: 20px;
      position: absolute;
      right: ${space(3)};
      left: ${space(3)};
      z-index: 1;
    }
    &:after {
      height: 40px;
    }
    &:before {
      top: 0;
      background: linear-gradient(
        180deg,
        rgba(16, 25, 33, 1) 33%,
        rgba(16, 25, 33, 0) 90%
      );
    }

    &:after {
      bottom: 0;
      background: linear-gradient(
        0,
        rgba(16, 25, 33, 1) 33%,
        rgba(16, 25, 33, 0) 90%
      );
    }
    ${dark &&
    css`
      &:before {
        background: linear-gradient(
          180deg,
          rgba(20, 20, 19, 1) 33%,
          rgba(20, 20, 19, 0) 90%
        );
        top: 0;
      }

      &:after {
        background: linear-gradient(
          0,
          rgba(20, 20, 19, 1) 33%,
          rgba(20, 20, 19, 0) 90%
        );
        bottom: 0;
      }
    `}
  }
  .${classNamePrefix}__menu-list {
    max-height: calc(var(--vh) * 0.5);
    scrollbar-color: ${color('interactiveSecondary')} transparent;
    &::-webkit-scrollbar {
      background: transparent;
      width: ${space(3)};
    }

    &::-webkit-scrollbar-thumb:vertical {
      background-color: ${color('interactiveSecondary')};
      border-radius: ${space(3)};
      background-clip: padding-box;
      border: inset transparent ${space(1)};
    }
    padding: ${space(2)} 0 ${space(4)};
  }
  .${classNamePrefix}__option {
    color: ${color('interactive')};
    width: auto;
    font-size: 17px;
    padding: ${space(3)} ${space(4)};
    font-weight: ${fontWeight('base')};
    position: relative;
    background-color: transparent;
    &:after {
      margin-top: ${space(3)};
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      width: auto;
      left: ${space(4)};
      right: ${space(4)};
      height: 1px;
      transition: height 200ms ease-in;
      background: ${color('interactive')};
    }
    &:hover {
      &:after {
        height: 4px;
      }
    }
    &:active {
      background-color: transparent !important;
    }
  }
  .${classNamePrefix}__option--is-focused {
    background-color: transparent;
  }
  .${classNamePrefix}__option--is-selected {
    font-weight: ${fontWeight('bold')};
    color: var(--control-color-selected);
    color: var(--control-color-selected);
    background-color: transparent;
    &:after {
      height: 4px;
      background: var(--control-color-selected);
    }
  }

  .${classNamePrefix}__control {
    ${SubheadlineStyle};
    margin-bottom: 0;
    border: 2px solid var(--control-border);
    background-color: transparent;
    box-shadow: none;
    color: var(--control-color);
    cursor: pointer;
    flex-direction: row;
    min-height: var(--control-min-height);
    padding: 0;
    border-radius: 32px;
    font-family: ${font('extended')};
    font-size: 0.823rem;
    font-weight: ${fontWeight('bold')};
    letter-spacing: 0.06em;
    line-height: 1.2;
    text-transform: uppercase;
    transition: border 220ms ease-in-out;

    &.iconButton {
      ${iconButtonStyles};
      --control-value-left-padding: 0;
      --control-value-right-padding: 0;
      border: none;
      align-items: center;
      color: ${color('interactiveSecondary')};
      font-family: ${font('base')};
      font-weight: ${fontWeight('base')};
      display: flex;
      flex-direction: column;
      justify-content: center;
      letter-spacing: normal;
      text-transform: capitalize;
    }

    &:hover {
      color: ${color('interactive')};
    }

    &:not(.iconButton):hover {
      border-color: var(--control-border-hover);
      background: ${color('interactive')};
      color: ${color('secondaryText')};
      svg {
        fill: ${color('interactiveHover')};
      }
    }
  }

  .${classNamePrefix}__control--is-focused {
    border-color: var(--control-border-focus);

    &:hover {
      border-color: var(--control-border-focus);
    }
  }

  .${classNamePrefix}__control--menu-is-open {
    border-color: var(--control-border-focus);
    .${classNamePrefix}__dropdown-indicator svg {
      transform: rotate(180deg);
    }
  }

  .${classNamePrefix}__indicators {
    min-width: calc(${space(4)} - 2px);
    padding: 0 6px;
  }
  .iconButton {
    .icon {
      height: 22px;
    }
  }
  .iconButton .${classNamePrefix}__indicators {
    display: none;
  }

  .${classNamePrefix}__indicator-separator {
    background: none;
  }
  .${classNamePrefix}__value-container {
    padding-left: var(--control-value-left-padding);
    padding-right: var(--control-value-right-padding);
  }
  .${classNamePrefix}__single-value {
    color: inherit;
  }
  .${classNamePrefix}__dropdown-indicator {
    display: none;
  }
`

export const SelectContainer = styled.div<{
  dark?: boolean
  positionRight?: boolean
}>`
  ${({ dark, positionRight }) => selectStyles(dark, positionRight)}
`
